/**
 * DiagnosticDashboard — main dashboard combining NRR benchmark + journey map.
 * Reads all data from the pre-generated ClientConfig.
 */

import type { ClientConfig } from "../types/diagnostic";
import { NRRBenchmarkView } from "./NRRBenchmarkView";
import { JourneyMap } from "./JourneyMap";
import { Synthesis } from "./Synthesis";
import { ExportActions } from "./ExportActions";

interface DiagnosticDashboardProps {
  config: ClientConfig;
}

export function DiagnosticDashboard({ config }: DiagnosticDashboardProps) {
  return (
    <div className="min-h-screen bg-surface-secondary">
      {/* Top bar */}
      <header className="bg-white border-b border-surface-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-text-primary">
              {config.clientName}
            </h1>
            <p className="text-xs text-text-tertiary">
              NRR & CX Diagnostic · {config.subVertical}
              {config.revenueTier !== "All" ? ` · ${config.revenueTier}` : ""}
            </p>
          </div>
          <ExportActions
            clientName={config.clientName}
            shareSecret={config.shareSecret}
          />
        </div>
      </header>

      {/* Dashboard content */}
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* NRR Benchmark */}
        <NRRBenchmarkView
          peers={config.peers}
          quartileStats={config.quartileStats}
          clientNrr={config.clientNrr}
          clientNrrEstimated={config.clientNrrEstimated}
          clientNrrPeriod={config.clientNrrPeriod}
          clientNrrMethodology={config.clientNrrMethodology}
          clientNrrHistory={config.clientNrrHistory}
          clientName={config.clientName}
          subVertical={config.subVertical}
          revenueTier={config.revenueTier}
          nrrCommentary={config.nrrCommentary}
        />

        {/* Journey Map */}
        <JourneyMap
          stages={config.journeyStages}
          painPoints={config.painPoints}
          strengths={config.strengths}
          clientName={config.clientName}
        />
        {/* Synthesis */}
        {config.synthesis && (
          <Synthesis
            synthesis={config.synthesis}
            clientName={config.clientName}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-6 py-6 border-t border-surface-borderLight">
        <p className="text-xs text-text-muted text-center">
          Generated {new Date(config.generatedAt).toLocaleDateString()} ·
          Benchmark data sourced from SEC filings, earnings transcripts, and
          market data · CX insights from public review sources
        </p>
      </footer>
    </div>
  );
}
